import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Loading, StatusBadge, SectionCard } from "@/components/common/Primitives";
import { ContractForm } from "@/components/forms/ContractForm";
import { CustomerForm } from "@/components/forms/CustomerForm";
import { CommLogDialog } from "@/components/forms/CommLogDialog";
import { fmtEUR, fmtDate, fmtDateTime } from "@/lib/format";
import { CONTRACT_STATUS_MAP, CATEGORY_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Phone, Mail, MessageSquare, Plus, Edit, Building2, User, AlertTriangle,
  FileText, FolderArchive, Clock,
} from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [contractOpen, setContractOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [commOpen, setCommOpen] = useState(false);

  const { data: reg } = useQuery({ queryKey: ["registry"], queryFn: async () => (await api.get("/settings/registry")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => (await api.get(`/customers/${id}`)).data,
  });

  if (isLoading) return <Loading />;
  if (!data) return null;
  const c = data.customer;
  const name = c.type === "business" ? c.company_name : `${c.first_name} ${c.last_name}`.trim();
  const refetch = () => qc.invalidateQueries({ queryKey: ["customer", id] });

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value || "—"}</span></div>
  );

  return (
    <div>
      <button onClick={() => navigate("/kunden")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Zurück zu Kunden
      </button>

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            {c.type === "business" ? <Building2 className="h-6 w-6" /> : <User className="h-6 w-6" />}
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{c.customer_number}</span>
              <Badge variant="secondary" className="capitalize">{c.status}</Badge>
              <span>{c.type === "business" ? "Geschäftskunde" : "Privatkunde"}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.phone && <Button variant="outline" size="sm" asChild><a href={`tel:${c.phone}`}><Phone className="mr-1.5 h-4 w-4" />Anrufen</a></Button>}
          {c.email && <Button variant="outline" size="sm" asChild><a href={`mailto:${c.email}`}><Mail className="mr-1.5 h-4 w-4" />E-Mail</a></Button>}
          {can(user?.role, "communication:write") && <Button variant="outline" size="sm" onClick={() => setCommOpen(true)} data-testid="log-comm-button"><MessageSquare className="mr-1.5 h-4 w-4" />Notieren</Button>}
          {can(user?.role, "customers:write") && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-customer-button"><Edit className="mr-1.5 h-4 w-4" />Bearbeiten</Button>}
        </div>
      </div>

      {data.missing_info?.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" /> Fehlende Informationen: {data.missing_info.join(", ")}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="flex w-full flex-wrap justify-start">
              <TabsTrigger value="overview" data-testid="tab-overview">Übersicht</TabsTrigger>
              <TabsTrigger value="contracts" data-testid="tab-contracts">Verträge ({data.contracts.length})</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Dokumente ({data.documents.length})</TabsTrigger>
              <TabsTrigger value="communication" data-testid="tab-communication">Kommunikation</TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tab-tasks">Aufgaben ({data.tasks.length})</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Verlauf</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <SectionCard title="Adresse & Kontakt">
                <Row label="Straße" value={`${c.street} ${c.house_number}`.trim()} />
                <Row label="PLZ / Ort" value={`${c.postal_code} ${c.city}`.trim()} />
                <Row label="Land" value={c.country} />
                <Row label="E-Mail" value={c.email} />
                <Row label="Telefon" value={c.phone} />
                <Row label="Mobil" value={c.mobile} />
                <Row label="Sprache" value={c.language === "tr" ? "Türkisch" : "Deutsch"} />
                <Row label="Marketing-Einwilligung" value={c.consent_marketing ? "Ja" : "Nein"} />
                {c.notes && <div className="mt-3 rounded-md bg-muted/50 p-3 text-sm"><span className="text-xs font-medium uppercase text-muted-foreground">Notizen</span><p className="mt-1">{c.notes}</p></div>}
              </SectionCard>
            </TabsContent>

            <TabsContent value="contracts" className="mt-4">
              <div className="mb-3 flex justify-end">
                {can(user?.role, "contracts:write") && <Button size="sm" className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setContractOpen(true)} data-testid="add-contract-button"><Plus className="h-4 w-4" />Vertrag hinzufügen</Button>}
              </div>
              <div className="space-y-2">
                {data.contracts.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Keine Verträge vorhanden.</p>}
                {data.contracts.map((ct) => (
                  <div key={ct.id} onClick={() => navigate(`/vertraege/${ct.id}`)} className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 hover:bg-muted/40">
                    <div className="flex items-center gap-3 min-w-0"><FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0"><div className="truncate text-sm font-medium">{ct.provider} · {ct.tariff_name}</div>
                        <div className="text-xs text-muted-foreground">{CATEGORY_LABELS[ct.category] || ct.category} · {fmtEUR(ct.monthly_fee)}/Monat</div></div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3"><span className="text-xs text-muted-foreground">bis {fmtDate(ct.end_date)}</span><StatusBadge status={ct.status} map={CONTRACT_STATUS_MAP} /></div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <div className="space-y-2">
                {data.documents.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Keine Dokumente.</p>}
                {data.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0"><FolderArchive className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate text-sm">{d.filename}</span></div>
                    <Button variant="ghost" size="sm" asChild><a href={`${api.defaults.baseURL}/documents/${d.id}/download`} target="_blank" rel="noreferrer">Öffnen</a></Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="communication" className="mt-4">
              <div className="space-y-3">
                {data.communications.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Keine Kommunikation protokolliert.</p>}
                {data.communications.map((m) => (
                  <div key={m.id} className="rounded-md border border-border px-4 py-3">
                    <div className="flex items-center justify-between"><Badge variant="secondary" className="capitalize">{m.channel}</Badge><span className="text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</span></div>
                    {m.subject && <div className="mt-2 text-sm font-medium">{m.subject}</div>}
                    <p className="mt-1 text-sm text-muted-foreground">{m.body}</p>
                    {m.status === "entwurf_nicht_verbunden" && <Badge variant="outline" className="mt-2 text-[10px]">Entwurf · Integration nicht verbunden</Badge>}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <div className="space-y-2">
                {data.tasks.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Keine Aufgaben.</p>}
                {data.tasks.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-4 py-3">
                    <div className="min-w-0"><div className="truncate text-sm font-medium">{t.title}</div><div className="text-xs text-muted-foreground">Fällig: {fmtDate(t.due_date)}</div></div>
                    <Badge variant="secondary" className="capitalize">{t.status}</Badge>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <div className="space-y-3">
                {data.activity.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Kein Verlauf.</p>}
                {data.activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted"><Clock className="h-3.5 w-3.5 text-muted-foreground" /></span>
                    <div><span className="font-medium capitalize">{a.action}</span> · {a.entity_type} <div className="text-xs text-muted-foreground">{a.user_name} · {fmtDateTime(a.created_at)}</div></div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-4">
          <SectionCard title="Stammdaten">
            <Row label="Kundennr." value={c.customer_number} />
            <Row label="Betreuer" value={c.assigned_user_name} />
            <Row label="Angelegt" value={fmtDate(c.created_at)} />
            <Row label="Letzte Aktivität" value={fmtDate(c.last_activity_at)} />
          </SectionCard>
          <SectionCard title="Kennzahlen">
            <Row label="Verträge" value={data.contracts.length} />
            <Row label="Aktive Verträge" value={data.contracts.filter((x) => x.status === "aktiv").length} />
            <Row label="Mtl. Volumen" value={fmtEUR(data.contracts.filter((x) => x.status === "aktiv").reduce((s, x) => s + Number(x.monthly_fee || 0), 0))} />
          </SectionCard>
        </aside>
      </div>

      <ContractForm open={contractOpen} onOpenChange={setContractOpen} fixedCustomerId={id} registry={reg} onSaved={refetch} />
      <CustomerForm open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={refetch} />
      <CommLogDialog open={commOpen} onOpenChange={setCommOpen} customerId={id} onSaved={refetch} />
    </div>
  );
}
